import { deriveIdempotencyKey } from "./idempotency.js";
import type { Attribution, Consent, PixelConfig, PixelEvent, PixelEventType } from "./types.js";

/**
 * Assembles the PixelEvent envelope. Pure + async (the idempotency key is hashed). The product
 * id (for the product_view dedup key) is read from `properties.product_id`. Money in
 * `properties` is expected to already be minor units (the auto-capture converts at the edge).
 */
export interface BuildInput {
  config: PixelConfig;
  visitorId: string;
  sessionId: string;
  event: PixelEventType;
  properties?: Record<string, unknown>;
  attribution: Attribution;
  nowMs: number;
  consent?: Consent | undefined;
}

export async function buildEnvelope(i: BuildInput): Promise<PixelEvent> {
  const properties = i.properties ?? {};
  const productId =
    typeof properties.product_id === "string" ? properties.product_id : undefined;
  const disambiguator =
    i.event === "scroll_depth" && typeof properties.depth_pct === "number"
      ? String(properties.depth_pct)
      : i.event === "product_image_viewed" &&
          typeof properties.image_index === "number" &&
          typeof properties.gallery_action === "string"
        ? `${properties.gallery_action}:${properties.image_index}:${Math.floor(i.nowMs / 1000)}`
        : i.event === "product_image_viewed" && typeof properties.image_index === "number"
          ? String(properties.image_index)
          : undefined;
  const idempotency_key = await deriveIdempotencyKey({
    event: i.event,
    visitorId: i.visitorId,
    sessionId: i.sessionId,
    nowMs: i.nowMs,
    productId,
    disambiguator,
  });
  const consent = i.consent ?? i.config.consent;

  return {
    platform: i.config.platform,
    store_id: i.config.storeId,
    visitor_id: i.visitorId,
    session_id: i.sessionId,
    event: i.event,
    source: "client",
    timestamp: new Date(i.nowMs).toISOString(),
    properties,
    attribution: i.attribution,
    ...(consent ? { consent } : {}),
    idempotency_key,
  };
}
